import { useEffect, useMemo, useState } from 'react'
import {
  BM_COUNTRIES,
  BM_DIMENSIONS,
  BM_INDICES,
  BM_REGIONAL,
  BM_SECTORS,
  BM_SIZES,
  BM_SOURCES,
  BMC_COUNTRIES,
  BMC_INDUSTRIES,
  BMC_SIZES,
} from '@/content/benchmarkData.generated'
import { vitalityOf } from '@/lib/vitality'
import SitePhoto from '@/components/Photo'
import { localizeHref } from '@/i18n/href'
import { useI18n } from '@/i18n/I18nProvider'
import type { SectionOf } from './shared'

/**
 * The DAIMI benchmark explorer.
 *
 * Halil, 2026-09-15: digitopia.co's DAIMI report page carried a tool of
 * Kardelen's where you picked a sector and a country; it did not survive the
 * migration (it was JavaScript inside a WordPress page, so the crawl never saw
 * it as a URL and it is in no row of `migration/decisions.json`). He asked for
 * it back, on its own page under Resources, and for DMI and AIMI to be
 * readable beside DAIMI rather than folded into one number.
 *
 * A screenshot of the old page arrived a day later and named it: *The DAIMI
 * Vitality Chart*, a Velocity × Endurance scatter with country, industry and
 * size filters. That is `VitalityChart` below, and it is the piece he was
 * actually remembering — the index table and the picker answer the question he
 * asked in words.
 *
 * Three parts, and the split is deliberate:
 *
 * - **The index table is server-rendered.** It is the numbers Halil asked to
 *   see, it is small, and prerendering it means the figures are in the HTML
 *   before any JavaScript runs — indexable, and readable by somebody whose
 *   bundle never arrives. The scope switch enhances it on the client; without
 *   JavaScript the reader still gets the global table.
 * - **The Vitality Chart and the picker are client-only.** They share the same
 *   506 cells, which are 7 KB gzipped and wanted by exactly one of the site's
 *   272 documents, so they arrive by dynamic import — once, for both. This is
 *   the only code-split in the app and the note in
 *   `scripts/generate-benchmark.mjs` explains why it earned one.
 *
 * WHAT IS NOT HERE, so nobody adds it back without the missing input:
 *
 * - **No country-level DMI / AIMI / DAIMI.** They can be computed from topic
 *   scores and the AI section map, and globally that lands within 0.01 of the
 *   official figure — but 0.13 low for the US, because the official number
 *   weights by company and no export carries a company count per cell. One
 *   number at a coarser grain beats two that disagree.
 * - **No min / max.** Published per sector in the source, and at the sample
 *   sizes involved the extremes are individual companies' own scores. The
 *   averages are the benchmark; the tails are somebody's data.
 */

/**
 * Below this many companies a figure is not shown at all.
 *
 * Five is the floor Kagan approved on 2026-09-16. It is not decoration: the
 * regional table publishes Pharmaceuticals/APAC at n=2 and Public Services/APAC
 * at n=2, and an "average" of two companies, printed next to their sector and
 * region, is those two companies.
 */
const MIN_N = 5

/**
 * The value a filter carries when it is not filtering.
 *
 * A sentinel rather than `''`, because an empty `<option>` value reads as
 * "nothing chosen" to a form and as a real selection to a reader, and the
 * chart's opening state is deliberately *everything*.
 */
const ALL = '__all__'

/**
 * Global, the two countries with their own study, then the three regions.
 *
 * **The regions are why `MIN_N` is not decoration.** The first version of this
 * page showed only the first three scopes, where the smallest sector is six
 * companies — so the floor guarded nothing and a deliberate attempt to break it
 * could not. The regional table publishes Pharmaceuticals/APAC and Public
 * Services/APAC at **two companies each**, and an "average" of two, printed
 * beside their sector and region, is those two companies. Adding the regions
 * both answers a question people actually ask and gives the rule something to
 * do.
 *
 * Regional carries DAIMI only — DMI and AIMI were not published at that grain,
 * so those two columns render as em dashes rather than as invented numbers.
 */
const SCOPES = ['global', 'turkiye', 'us', 'APAC', 'Americas', 'EMEA'] as const
type Scope = (typeof SCOPES)[number]

const rowsFor = (scope: Scope) => BM_INDICES[scope] ?? BM_REGIONAL[scope] ?? {}

/** `[countryIndex, sectorIndex, sizeIndex, sourceIndex, ...six scores ×100]` */
type Cell = readonly number[]

function Score({ value }: { value: number | null | undefined }) {
  if (value == null) return <span className="text-muted-foreground">—</span>
  return <span className="tabular-nums font-medium">{value.toFixed(2)}</span>
}

/**
 * A 1–5 maturity score as a bar.
 *
 * The scale starts at 1 rather than 0 because the instrument does: nothing can
 * score below 1, so a bar measured from zero spends a fifth of its width saying
 * nothing and flattens every real difference between sectors.
 */
function Bar({ value }: { value: number }) {
  const pct = Math.max(0, Math.min(100, ((value - 1) / 4) * 100))
  return (
    <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
      <div className="h-full rounded-full bg-primary" style={{ width: `${pct}%` }} />
    </div>
  )
}

export default function BenchmarkSection({ section }: { section: SectionOf<'benchmark'> }) {
  const t = section.labels
  const [scope, setScope] = useState<Scope>('global')

  return (
    <section className="site-container py-12 sm:py-16">
      {/* The chart first. It was under the table until 2026-09-16, when Kagan
          said the striking picture belongs at the top — and he is right: the
          table is the reference you consult, this is the reason to. */}
      <VitalityChart labels={t} />
      <IndexTable scope={scope} setScope={setScope} labels={t} />
      <Picker labels={t} />
    </section>
  )
}

function IndexTable({
  scope,
  setScope,
  labels: t,
}: {
  scope: Scope
  setScope: (s: Scope) => void
  labels: SectionOf<'benchmark'>['labels']
}) {
  const rows = rowsFor(scope)
  const sectors = Object.keys(rows).filter((k) => k !== 'ALL')
  const all = rows.ALL

  return (
    <div className="mt-14 border-t border-border pt-10">
      <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">{t.indexHeading}</h2>
      <p className="mt-3 max-w-3xl text-muted-foreground">{t.indexBlurb}</p>

      {/* Radio inputs rather than buttons: three exclusive choices IS a radio
          group, so the keyboard and the screen reader get it for free, and the
          checked one survives without a click handler on each. */}
      <div role="group" aria-label={t.scopeLabel} className="mt-6 flex flex-wrap gap-2">
        {SCOPES.map((s) => (
          <label
            key={s}
            className={
              'cursor-pointer rounded-full border px-4 py-1.5 text-sm font-medium transition-colors ' +
              (scope === s
                ? 'border-primary bg-primary text-primary-foreground'
                : 'border-border hover:bg-muted')
            }
          >
            <input
              type="radio"
              name="bm-scope"
              className="sr-only"
              checked={scope === s}
              onChange={() => setScope(s)}
            />
            {t.scopes[s]}
          </label>
        ))}
      </div>

      {/* The one element allowed to scroll sideways on a phone, and it says so
          with its own container rather than letting the page do it. */}
      <div className="mt-6 overflow-x-auto">
        <table className="w-full min-w-[34rem] border-collapse text-sm">
          <thead>
            <tr className="border-b border-border text-left">
              <th scope="col" className="py-2 pr-4 font-bold">{t.colSector}</th>
              <th scope="col" className="py-2 pr-4 text-right font-bold">{t.colCompanies}</th>
              <th scope="col" className="py-2 pr-4 text-right font-bold">DAIMI</th>
              <th scope="col" className="py-2 pr-4 text-right font-bold">DMI</th>
              <th scope="col" className="py-2 text-right font-bold">AIMI</th>
            </tr>
          </thead>
          <tbody>
            {all && (
              <tr className="border-b border-border bg-muted/40 font-medium">
                <th scope="row" className="py-2 pr-4 text-left">{t.allSectors}</th>
                <td className="py-2 pr-4 text-right tabular-nums">{all.n}</td>
                <td className="py-2 pr-4 text-right"><Score value={all.daimi} /></td>
                <td className="py-2 pr-4 text-right"><Score value={all.dmi} /></td>
                <td className="py-2 text-right"><Score value={all.aimi} /></td>
              </tr>
            )}
            {sectors.map((name) => {
              const r = rows[name]
              const thin = r.n < MIN_N
              return (
                <tr key={name} className="border-b border-border/60">
                  <th scope="row" className="py-2 pr-4 text-left font-normal">{name}</th>
                  <td className="py-2 pr-4 text-right tabular-nums text-muted-foreground">{r.n}</td>
                  {thin ? (
                    <td colSpan={3} className="py-2 text-right text-xs text-muted-foreground">
                      {t.tooFew}
                    </td>
                  ) : (
                    <>
                      <td className="py-2 pr-4 text-right"><Score value={r.daimi} /></td>
                      <td className="py-2 pr-4 text-right"><Score value={r.dmi} /></td>
                      <td className="py-2 text-right"><Score value={r.aimi} /></td>
                    </>
                  )}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      <p className="mt-3 text-xs text-muted-foreground">{t.indexNote}</p>
    </div>
  )
}

/**
 * Country × sector × size, over the six dimensions.
 *
 * Client-only by construction: the cells arrive through a dynamic import in an
 * effect, so the server renders the heading and a waiting line and the browser
 * fills it in. A visitor with no JavaScript loses this half and keeps the index
 * table above, which is the right way round — the table is the answer, this is
 * the exploration.
 */
function Picker({ labels: t }: { labels: SectionOf<'benchmark'>['labels'] }) {
  const [cells, setCells] = useState<readonly Cell[] | null>(null)
  const [failed, setFailed] = useState(false)
  const [country, setCountry] = useState('Türkiye')
  const [sector, setSector] = useState('Banking')
  const [size, setSize] = useState('L')

  useEffect(() => {
    let live = true
    import('@/content/benchmarkCells.generated')
      .then((m) => live && setCells(m.BM_CELLS))
      // A chunk that will not load is a real state, not a theoretical one —
      // a flaky connection, a stale deployment, an aggressive proxy. Say so
      // rather than leaving the reader looking at "loading" forever.
      .catch(() => live && setFailed(true))
    return () => {
      live = false
    }
  }, [])

  /**
   * Only offer a sector that actually has a row for the chosen country, and
   * only a size that has one for that pair.
   *
   * Narrowing the lists is half the job. The other half is below, and it is
   * the half that matters: **the benchmark is sparse.** The opening view is
   * Türkiye / Banking / Large, and Banking/Large exists for 26 of the 46
   * countries — so without a correction, twenty of them would answer a country
   * change with "no companies match", on a tool whose entire purpose is
   * browsing. Germany, to pick one, has ten cells and Banking only in Small.
   */
  const available = useMemo(() => {
    if (!cells) return { sectors: BM_SECTORS as readonly string[], sizes: BM_SIZES as readonly string[] }
    const ci = BM_COUNTRIES.indexOf(country as (typeof BM_COUNTRIES)[number])
    const mine = cells.filter((c) => c[0] === ci)
    return {
      sectors: [...new Set(mine.map((c) => BM_SECTORS[c[1]]))].sort(),
      // Kept in BM_SIZES order (S, M, L) rather than in the order the rows
      // happen to appear, so the control does not reshuffle as you browse.
      sizes: BM_SIZES.filter((z) =>
        mine.some((c) => BM_SECTORS[c[1]] === sector && BM_SIZES[c[2]] === z),
      ),
    }
  }, [cells, country, sector])

  // Snap an impossible selection back to a possible one. Two effects rather
  // than one, because the sector has to settle before the sizes it allows can
  // be judged — collapsing them means reading a `sizes` list computed for the
  // sector we are in the middle of leaving.
  useEffect(() => {
    if (cells && available.sectors.length && !available.sectors.includes(sector)) {
      setSector(available.sectors[0])
    }
  }, [cells, available.sectors, sector])

  useEffect(() => {
    if (cells && available.sizes.length && !available.sizes.includes(size)) {
      setSize(available.sizes[0])
    }
  }, [cells, available.sizes, size])

  const match = useMemo(() => {
    if (!cells) return null
    const ci = BM_COUNTRIES.indexOf(country as (typeof BM_COUNTRIES)[number])
    const si = BM_SECTORS.indexOf(sector as (typeof BM_SECTORS)[number])
    const zi = BM_SIZES.indexOf(size as (typeof BM_SIZES)[number])
    return cells.find((c) => c[0] === ci && c[1] === si && c[2] === zi) ?? null
  }, [cells, country, sector, size])

  const select =
    'w-full rounded-[--radius] border border-border bg-background px-3 py-2 text-sm'

  return (
    <div id="benchmark-picker" className="mt-14 scroll-mt-24 border-t border-border pt-10">
      <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">{t.pickerHeading}</h2>
      <p className="mt-3 max-w-3xl text-muted-foreground">{t.pickerBlurb}</p>

      {failed ? (
        <p className="mt-6 text-sm text-muted-foreground">{t.failed}</p>
      ) : !cells ? (
        <p className="mt-6 text-sm text-muted-foreground">{t.loading}</p>
      ) : (
        <>
          <div className="mt-6 grid gap-4 sm:grid-cols-3">
            <label className="block">
              <span className="mb-1.5 block text-sm font-medium">{t.country}</span>
              <select className={select} value={country} onChange={(e) => setCountry(e.target.value)}>
                {BM_COUNTRIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="mb-1.5 block text-sm font-medium">{t.sector}</span>
              <select className={select} value={sector} onChange={(e) => setSector(e.target.value)}>
                {available.sectors.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="mb-1.5 block text-sm font-medium">{t.size}</span>
              <select className={select} value={size} onChange={(e) => setSize(e.target.value)}>
                {(available.sizes.length ? available.sizes : BM_SIZES).map((z) => (
                  <option key={z} value={z}>{t.sizes[z as 'S' | 'M' | 'L']}</option>
                ))}
              </select>
            </label>
          </div>

          {match ? (
            <div className="mt-8">
              <dl className="space-y-4">
                {BM_DIMENSIONS.map((dim, i) => {
                  const v = match[4 + i] / 100
                  return (
                    <div key={dim} className="grid grid-cols-[8rem_1fr_3rem] items-center gap-3 sm:grid-cols-[10rem_1fr_3.5rem] sm:gap-4">
                      <dt className="text-sm font-medium">{dim}</dt>
                      <dd className="min-w-0"><Bar value={v} /></dd>
                      <dd className="text-right text-sm tabular-nums font-medium">{v.toFixed(2)}</dd>
                    </div>
                  )
                })}
              </dl>
              {/* The chart's two axes, for this cell, from these six numbers.
                  This is where a derived figure belongs — next to what it was
                  derived from — and it is what ties the two halves of the page
                  together: the scatter above plots companies, and this says
                  which three dimensions each of its axes is made of.

                  Derived, and labelled as such: the companies file carries
                  Velocity and Endurance per company but its cell coverage is
                  not this file's (603 groups against 506), so the two cannot be
                  used interchangeably. `gate-test.mjs` checks these against the
                  companies for every group both files have. */}
              {(() => {
                const { velocity, endurance } = vitalityOf(
                  BM_DIMENSIONS,
                  BM_DIMENSIONS.map((_, i) => match[4 + i] / 100),
                )
                return (
                  <dl className="mt-6 flex flex-wrap gap-x-8 gap-y-2 border-t border-border pt-4 text-sm">
                    <div className="flex gap-2">
                      <dt className="font-medium">{t.axisVelocity}</dt>
                      <dd className="tabular-nums">{velocity.toFixed(2)}</dd>
                    </div>
                    <div className="flex gap-2">
                      <dt className="font-medium">{t.axisEndurance}</dt>
                      <dd className="tabular-nums">{endurance.toFixed(2)}</dd>
                    </div>
                    <p className="text-muted-foreground">{t.derivedNote}</p>
                  </dl>
                )
              })()}
              {/* Which study this row came from. Not decoration: most countries
                  here arrive through one regional study rather than through a
                  single global panel, and a reader who takes the number for a
                  worldwide sample is reading it wrong. */}
              <p className="mt-6 text-xs text-muted-foreground">
                {t.sourceLine.replace('{source}', BM_SOURCES[match[3]])}
              </p>
            </div>
          ) : (
            <p className="mt-8 text-sm text-muted-foreground">{t.noData}</p>
          )}
        </>
      )}
    </div>
  )
}

/**
 * The DAIMI Vitality Chart — the real one, on the real 1,575 companies.
 *
 * WHAT HAPPENED HERE, because the order matters. The chart was first built on
 * the 506 (country, sector, size) cells, with Velocity and Endurance derived
 * from the six dimensions — the formula recovered from five companies, see
 * `@/lib/vitality`. That was the right thing to do with the data in hand.
 *
 * Then Fatih sent `daimi-grafik-tasima.zip`: the original page's chart block,
 * its assets, and **`daimireport26_velocity-endurance-scatter_data-1.json` —
 * 1,575 rows carrying Velocity, Endurance and DAIMI per company.** So the chart
 * is now what it was: one dot per company.
 *
 * **That file no longer exists anywhere else.** `digitopia.co` 301s to
 * `www.digitopia.ai` now, including the `/wp-content/uploads/…` path the old
 * chart fetched from, and the new site 404s it. The live chart on the old page
 * had already stopped working. `migration/benchmark/daimi-companies.json` is
 * the surviving copy and is committed for that reason.
 *
 * The derived formula was not wasted and is not now unverifiable — the
 * opposite. Its global means come out at 2.917 and 2.934 against the published
 * 2.92 and 2.93, and `gate-test.mjs` now checks every group it can against the
 * companies that make it up. It still drives the picker's Velocity/Endurance
 * line, which is the honest place for a derived figure: beside the dimensions
 * it was derived from.
 *
 * **THIS IS THE FIRST THING ON THE PAGE.** It was under the index table until
 * Kagan said the striking picture should be at the top, which is right — the
 * table is the reference and this is the reason to read it. That is what the
 * static/dynamic split below buys: the frame, the axes, the filters and the
 * corner labels are in the prerendered HTML, so the top of the page is a chart
 * before any script runs. Only the dots arrive by import.
 *
 * FAITHFUL TO THE ORIGINAL: 1–5 axes with whole-number ticks, one dot per
 * company, three filters that combine, the live company count, a reset, a
 * seven-field hover panel, and the Industry Focus card when a sector is chosen.
 *
 * DELIBERATELY NOT FAITHFUL, and each is worth knowing:
 *
 * - **No Chart.js.** The original loaded 4.4.1 from a CDN — about 200 KB for a
 *   scatter plot of circles. This renders SVG, which costs nothing, works in
 *   the prerender, and needs no third-party origin at runtime. Nothing else on
 *   this site draws a chart, so a charting dependency would exist for this one
 *   component.
 * - **The report card links to this site's own report**, not to
 *   `digitopia.co/impact-labs/reports/…`. All seventeen exist here; the map is
 *   `INDUSTRY_REPORT` below and `gate-test.mjs` refuses a slug or a cover that
 *   is not really there, so no link on it can be invented.
 * - **The quadrant lines and the four profiles are an addition.** The original
 *   chart block had neither — Iron Man, Marathon Runner, Sprinter and Walker
 *   were boxes elsewhere on the page, with nothing tying them to the dots.
 * - **DAIMI / DMI / AIMI pills, which filter rather than colour** (`IndexStrip`,
 *   `IndexLegend`). Halil asked for DMI and AIMI readable beside DAIMI; Kagan
 *   asked for them as toggles that change the chart. Pressing one leaves that
 *   index and only that index — one number in the box, one in the hover panel,
 *   and the 103 companies with no split off the chart entirely. The derivation
 *   is in `generate-benchmark.mjs`.
 *
 *   **They are not axes**, and the reason is worth keeping: DMI and AIMI *would*
 *   make a plane together, but Velocity and Endurance split the six
 *   *dimensions* while DMI and AIMI split the 128 *topics* by section, so a
 *   pair taken one from each cut means nothing. A DMI × AIMI chart is a
 *   different chart, not this one with different labels.
 * - **Country names follow the rest of this page**: `United States` rather than
 *   the original's `USA`, with the source's three odd spellings normalised in
 *   the generator. The priority order (United States, Türkiye, Germany, then
 *   alphabetical) is the original's.
 */
function VitalityChart({ labels: t }: { labels: SectionOf<'benchmark'>['labels'] }) {
  const [mod, setMod] = useState<CompaniesModule | null>(null)
  const [failed, setFailed] = useState(false)
  const [country, setCountry] = useState(ALL)
  const [industry, setIndustry] = useState(ALL)
  const [size, setSize] = useState(ALL)
  const [index, setIndex] = useState<IndexKey>('daimi')

  useEffect(() => {
    let live = true
    import('@/content/benchmarkCompanies.generated')
      .then((m) => live && setMod(m))
      .catch(() => live && setFailed(true))
    return () => {
      live = false
    }
  }, [])

  const points = useMemo(() => {
    if (!mod) return []
    return mod.BM_COMPANIES.map((c, row) => ({
      id: mod.bmcCompanyId(row),
      country: BMC_COUNTRIES[c[0]],
      industry: BMC_INDUSTRIES[c[1]],
      size: BMC_SIZES[c[2]],
      velocity: c[3] / 100,
      endurance: c[4] / 100,
      daimi: c[5] / 100,
      // Zero means the company's group has no topic export, so there is no
      // split to show. Null rather than 0, because 0 is off the 1–5 scale and
      // would sort and colour as "very low" instead of "unknown".
      aimi: c[6] ? c[6] / 100 : null,
      dmi: c[7] ? c[7] / 100 : null,
    }))
  }, [mod])

  /**
   * The dots on screen.
   *
   * **The index is a filter, not a tint** (Kagan, 2026-09-16: *"AIMI butonuna
   * basınca sadece AIMI verileri kalır, keza diğerleri de öyle"*). Choosing DMI
   * or AIMI therefore drops the 103 companies whose group has no split in the
   * topic export — they have no such figure, so they are not part of that
   * picture — while DAIMI keeps all 1,575. The count beside the chart moves,
   * which is the point: the reader can see what the question costs.
   */
  const shown = useMemo(
    () =>
      points.filter(
        (p) =>
          (country === ALL || p.country === country) &&
          (industry === ALL || p.industry === industry) &&
          (size === ALL || p.size === size) &&
          indexValue(p, index) != null,
      ),
    [points, country, industry, size, index],
  )

  const dropped = useMemo(
    () =>
      index === 'daimi'
        ? 0
        : points.filter(
            (p) =>
              (country === ALL || p.country === country) &&
              (industry === ALL || p.industry === industry) &&
              (size === ALL || p.size === size) &&
              indexValue(p, index) == null,
          ).length,
    [points, country, industry, size, index],
  )

  const counts = { ironMan: 0, marathon: 0, sprinter: 0, walker: 0 }
  for (const p of shown) counts[profileOf(p.velocity, p.endurance)]++

  const reset = () => {
    setCountry(ALL)
    setIndustry(ALL)
    setSize(ALL)
    setIndex('daimi')
  }

  const select =
    'w-full rounded-full border border-primary bg-primary/5 px-4 py-2.5 text-sm text-primary'

  return (
    // Both blocks on this page carry an id, and it is load-bearing twice over:
    // it is what a link from a report can point at, and it is what the browser
    // check scopes its selectors to. Before the chart existed the check said
    // "the last section with a select"; the chart put three more selects in the
    // same section and the check went on passing while driving the wrong one.
    <div id="vitality-chart" className="scroll-mt-24">
      <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">{t.vitalityHeading}</h2>
      <p className="mt-3 max-w-3xl text-muted-foreground">{t.vitalityBlurb}</p>

      {/* The original's layout: the square plot, with the three filters and the
          reset stacked beside it, dropping under the chart on a phone. All of
          it renders server-side — the lists are static and the frame needs no
          data — so what arrives late is dots, not the page. */}
      <div className="mt-8 grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_16rem]">
        <div className="min-w-0">
          <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
            <h3 className="text-lg font-semibold">{t.figureTitle}</h3>
            <p className="text-sm">
              {t.companyCount}{' '}
              <span className="font-bold tabular-nums">{mod ? shown.length : '—'}</span>
            </p>
          </div>
          <Scatter points={shown} labels={t} counts={counts} index={index} />
          {failed && <p className="mt-3 text-sm text-muted-foreground">{t.failed}</p>}
        </div>

        <div className="flex flex-col gap-4">
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium">{t.country}</span>
            <select className={select} value={country} onChange={(e) => setCountry(e.target.value)}>
              <option value={ALL}>{t.allOption}</option>
              {CHART_COUNTRIES.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium">{t.industry}</span>
            <select className={select} value={industry} onChange={(e) => setIndustry(e.target.value)}>
              <option value={ALL}>{t.allOption}</option>
              {BMC_INDUSTRIES.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium">{t.size}</span>
            <select className={select} value={size} onChange={(e) => setSize(e.target.value)}>
              <option value={ALL}>{t.allOption}</option>
              {BMC_SIZES.map((z) => (
                <option key={z} value={z}>{z}</option>
              ))}
            </select>
          </label>
          {/* Pills rather than a fourth dropdown, and radios rather than
              buttons — three exclusive choices IS a radio group, so the
              keyboard and the screen reader come free. Same shape as the
              scope switch on the index table below, because it is the same
              kind of question. */}
          <div>
            <span className="mb-1.5 block text-sm font-medium">{t.indexLabel}</span>
            <div role="group" aria-label={t.indexLabel} className="flex flex-wrap gap-2">
              {INDEX_KEYS.map((k) => (
                <label
                  key={k}
                  className={
                    'cursor-pointer rounded-full border px-4 py-1.5 text-sm font-medium transition-colors ' +
                    (index === k
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'border-border hover:bg-muted')
                  }
                >
                  <input
                    type="radio"
                    name="bm-index"
                    // The value is the key, not left to default to `on`. Three
                    // radios that all report `on` are indistinguishable to a
                    // form, to a test, and to anything reading the DOM.
                    value={k}
                    className="sr-only"
                    checked={index === k}
                    onChange={() => setIndex(k)}
                  />
                  {t.indexNames[k]}
                </label>
              ))}
            </div>
          </div>

          <button
            type="button"
            onClick={reset}
            className="rounded-full bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90"
          >
            {t.resetFilter}
          </button>

          <IndexStrip points={shown} index={index} ready={!!mod} labels={t} />
        </div>
      </div>

      {/* The four profiles, with what each one means and how many of the
          visible companies sit there. A legend that only names the corners
          makes the reader guess; this is the part Halil's colleagues actually
          quote back. */}
      <IndexLegend index={index} points={shown} dropped={dropped} labels={t} />

      <dl className="mt-8 grid gap-x-8 gap-y-4 sm:grid-cols-2">
        {PROFILES.map((key) => (
          <div key={key} className="flex gap-3">
            {/* No colour chip. The dots are split by the chosen index now, so
                a chip here would name a colour that is not on the chart. The
                counts stay: they are about where a company sits, which the
                index does not change. */}
            <div className="min-w-0">
              <dt className="text-sm font-semibold">
                {t.profiles[key].name}{' '}
                <span className="font-normal tabular-nums text-muted-foreground">
                  ({mod ? counts[key] : '—'})
                </span>
              </dt>
              <dd className="mt-0.5 text-sm text-muted-foreground">{t.profiles[key].blurb}</dd>
            </div>
          </div>
        ))}
      </dl>

      <IndustryReportCard industry={industry} labels={t} />

      <p className="mt-6 text-xs text-muted-foreground">{t.vitalityNote}</p>
    </div>
  )
}

/**
 * The original's country order: the three the report is mostly about, then the
 * rest alphabetically. Kept because a reader looking for Türkiye in a list of
 * forty-six should not have to scroll to the T's.
 */
const CHART_PRIORITY = ['United States', 'Türkiye', 'Germany']
const CHART_COUNTRIES = [
  ...CHART_PRIORITY.filter((c) => (BMC_COUNTRIES as readonly string[]).includes(c)),
  ...BMC_COUNTRIES.filter((c) => !CHART_PRIORITY.includes(c)),
]

/**
 * The one index the chart is currently reading, for the companies on screen.
 *
 * **Halil's request and Kagan's together, and it took two days and two files.**
 * Halil, 2026-09-15: DMI and AIMI readable beside DAIMI rather than folded into
 * one number. Kagan, 2026-09-16: *"DAIMI / DMI / AIMI diye düğmeler toggle
 * olsa, shade etmese filtrelese?"* — and, when a first version washed the dots
 * in a colour ramp instead, *"AIMI butonuna basınca sadece AIMI verileri kalır,
 * keza diğerleri de öyle."*
 *
 * So the pills filter the *question*, they do not tint the answer. Pressing
 * AIMI leaves AIMI: this box shows that one number, the hover panel shows that
 * one number, and the companies whose group has no AI/digital split leave the
 * chart, because they have no such figure to be on it with.
 *
 * What made any of it possible was a column nobody was looking for. The split
 * is computable from the topic export and the section map, and doing so
 * reproduced the global AIMI but came out **0.13 low for the United States** —
 * because the published figure weights by company and no export carried a
 * company count per group. Fatih's scatter file is 1,575 rows of one company
 * each, so it *is* that count. Weighted, the same arithmetic lands on 2.433
 * against a published 2.45. See `generate-benchmark.mjs`.
 */
function IndexStrip({
  points,
  index,
  ready,
  labels: t,
}: {
  points: Company[]
  index: IndexKey
  ready: boolean
  labels: SectionOf<'benchmark'>['labels']
}) {
  // The five-company floor, same as the table below and for the same reason:
  // an average of two companies, printed beside their country and sector, is
  // those two companies.
  const enough = points.length >= MIN_N
  const value = enough
    ? points.reduce((a, p) => a + (indexValue(p, index) ?? 0), 0) / points.length
    : null

  return (
    <div className="rounded-[--radius] border border-border p-4">
      <h4 className="text-sm font-semibold">
        {t.indexNames[index]}
        {index !== 'daimi' && (
          <span className="font-normal text-muted-foreground">{ESTIMATE_MARK}</span>
        )}
      </h4>
      {!ready ? (
        <p className="mt-2 text-sm text-muted-foreground">{t.loading}</p>
      ) : !enough ? (
        <p className="mt-2 text-sm text-muted-foreground">{t.tooFew}</p>
      ) : (
        <>
          <p className="mt-1 text-3xl font-bold tabular-nums">{value!.toFixed(2)}</p>
          <p className="mt-2 text-xs text-muted-foreground">
            {t.indexAverage
              .replace('{n}', String(points.length))
              .replace('{index}', t.indexNames[index])}
          </p>
        </>
      )}
    </div>
  )
}

/** The three indices, in the order they are read. */
const INDEX_KEYS = ['daimi', 'dmi', 'aimi'] as const
type IndexKey = (typeof INDEX_KEYS)[number]

const indexValue = (p: Company, index: IndexKey): number | null =>
  index === 'daimi' ? p.daimi : index === 'dmi' ? p.dmi : p.aimi

/**
 * The benchmark-wide average of each index, which is where the dots divide.
 *
 * Fixed, and over every company rather than the visible ones, for the reason
 * the axes are fixed: a divider that moves as you filter makes every selection
 * come out half ahead and half behind. DAIMI's is the figure Pelin published;
 * the other two are the means of the estimates, which reproduce hers to 0.01.
 * `gate-test.mjs` asserts all three against the data.
 */
const INDEX_AVERAGE: Record<IndexKey, number> = {
  daimi: 2.93,
  dmi: 3.05,
  aimi: 2.38,
}

/**
 * Two states, not a ramp.
 *
 * The version before this one was a `color-mix` gradient across the index's
 * whole range, which is what *"shade etmese"* ruled out: a wash makes a
 * picture and answers nothing you can point at. Ahead of the benchmark or
 * behind it is a question with an answer, and both counts are printed.
 */
function IndexLegend({
  index,
  points,
  dropped,
  labels: t,
}: {
  index: IndexKey
  points: Company[]
  dropped: number
  labels: SectionOf<'benchmark'>['labels']
}) {
  const above = points.filter((p) => (indexValue(p, index) ?? 0) >= INDEX_AVERAGE[index]).length
  return (
    <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-muted-foreground">
      <span className="flex items-center gap-2">
        <span aria-hidden="true" className="h-2.5 w-2.5 rounded-full bg-primary" />
        {t.aboveAverage
          .replace('{n}', String(above))
          .replace('{index}', t.indexNames[index])
          .replace('{avg}', INDEX_AVERAGE[index].toFixed(2))}
      </span>
      <span className="flex items-center gap-2">
        <span aria-hidden="true" className="h-2.5 w-2.5 rounded-full bg-border" />
        {t.belowAverage.replace('{n}', String(points.length - above))}
      </span>
      {index !== 'daimi' && <span>{t.estimatedNote}</span>}
      {dropped > 0 && <span>{t.noSplit.replace('{n}', String(dropped))}</span>}
    </div>
  )
}

/** The shape of the dynamically imported companies module. */
type CompaniesModule = typeof import('@/content/benchmarkCompanies.generated')
/**
 * The seventeen Industry Focus reports, as this site's own slugs and covers.
 *
 * The original card pointed at `digitopia.co/impact-labs/reports/…` and at
 * `wp-content/uploads/…` images. Both are dead links now — that host redirects
 * here — and all seventeen reports exist on this site, so the card points at
 * them instead.
 *
 * **Hardcoded rather than read from `@/content/reports`**, which would pull the
 * whole report registry and every report's copy into the bundle for a page that
 * wants two strings. The cost of hardcoding is that it can drift, so it does
 * not get to: `gate-test.mjs` reads the `DAIMI_*_SLUG` constants and the
 * `public/photo` directory and fails on a slug or a cover that is not there,
 * and on an industry in the data that has no entry here.
 */
const INDUSTRY_REPORT: Record<string, { slug: string; art: string }> = {
  Airlines: { slug: 'digital-and-ai-maturity-report-2026-airlines-industry', art: 'daimi-report-2026-airlines-cover' },
  Airports: { slug: 'digital-and-ai-maturity-report-2026-airports-industry', art: 'daimi-report-2026-airports-cover' },
  Automotive: { slug: 'digital-and-ai-maturity-report-automotive-2026', art: 'daimi-report-2026-automotive-cover' },
  Banking: { slug: 'digital-and-ai-maturity-report-2026-banking-industry', art: 'daimi-report-2026-banking-cover' },
  Construction: { slug: 'digital-and-ai-maturity-report-2026-construction-industry', art: 'daimi-report-2026-construction-cover' },
  'Consumer Goods': { slug: 'digital-and-ai-maturity-report-2026-consumer-goods-industry', art: 'daimi-report-2026-consumer-goods-cover' },
  Energy: { slug: 'digital-and-ai-maturity-report-2026-energy-industry', art: 'daimi-report-2026-energy-cover' },
  Insurance: { slug: 'digital-and-ai-maturity-report-2026-insurance-industry', art: 'daimi-report-2026-insurance-cover' },
  Logistics: { slug: 'digital-and-ai-maturity-report-2026-logistics-industry', art: 'daimi-report-2026-logistics-cover' },
  Manufacturing: { slug: 'digital-and-ai-maturity-report-2026-manufacturing-industry', art: 'daimi-report-2026-manufacturing-cover' },
  Pharmaceuticals: { slug: 'digital-and-ai-maturity-report-2026-pharmaceuticals-industry', art: 'daimi-report-2026-pharmaceuticals-cover' },
  'Public Services': { slug: 'digital-and-ai-maturity-report-2026-public-services-industry', art: 'daimi-report-2026-public-services-cover' },
  Retail: { slug: 'digital-and-ai-maturity-report-2026-retail-industry', art: 'daimi-report-2026-retail-cover' },
  Services: { slug: 'digital-and-ai-maturity-report-2026-services-industry', art: 'daimi-report-2026-services-cover' },
  Technology: { slug: 'digital-and-ai-maturity-report-2026-technology-industry', art: 'daimi-report-2026-technology-cover' },
  Tourism: { slug: 'digital-and-ai-maturity-report-2026-tourism-industry', art: 'daimi-report-2026-tourism-cover' },
  Utilities: { slug: 'digital-and-ai-maturity-report-2026-utilities-industry', art: 'daimi-report-2026-utilities-cover' },
}

function IndustryReportCard({
  industry,
  labels: t,
}: {
  industry: string
  labels: SectionOf<'benchmark'>['labels']
}) {
  const { locale } = useI18n()
  const report = INDUSTRY_REPORT[industry]
  if (!report) return null
  return (
    <a
      // `/en/…` and then localised, which is the dictionary's convention: every
      // internal path in this app is written once in English and rewritten at
      // render. Passing an unprefixed path returns it unchanged, so the link
      // works in English and drops out of Turkish — which is exactly the shape
      // `check-benchmark.mjs` caught on the first run.
      href={localizeHref(`/en/impact-labs/reports/${report.slug}`, locale)}
      className="mt-8 grid grid-cols-[5.5rem_minmax(0,1fr)] items-center gap-5 rounded-[--radius] bg-gradient-to-br from-[hsl(var(--sidebar-background))] to-primary p-5 transition-transform hover:-translate-y-0.5 sm:grid-cols-[7.5rem_minmax(0,1fr)]"
    >
      {/* The report's own cover, through the manifest — so a phone downloads
          the 400px rendition of a 2560px original rather than the original. */}
      <SitePhoto
        photo={{ src: `/photo/${report.art}.webp`, alt: '' }}
        sizes="(min-width: 640px) 7.5rem, 5.5rem"
        className="w-full rounded-md"
      />
      <div className="min-w-0">
        <p className="text-xs uppercase tracking-wider text-primary-foreground/70">
          {t.reportEyebrow}
        </p>
        <p className="mt-1.5 text-lg font-semibold leading-tight text-primary-foreground sm:text-xl">
          {t.reportTitle.replace('{industry}', industry)}
        </p>
        <span className="mt-3.5 inline-flex rounded-full bg-background px-4 py-2 text-sm font-semibold text-primary">
          {t.reportButton}
        </span>
      </div>
    </a>
  )
}

/** What the four corners are called, reading from the top right anticlockwise. */
const PROFILES = ['ironMan', 'marathon', 'sprinter', 'walker'] as const
type Profile = (typeof PROFILES)[number]

/*
 * The four profiles no longer carry a colour of their own.
 *
 * They had one while the dots were coloured by quadrant, which was the chart's
 * only reading. Now the dots are split by whether a company is ahead of the
 * benchmark on the chosen index, and a second colour scheme on the same marks
 * would be two keys for one picture. The corners keep their names, their
 * positions and their counts — which is what they were ever about.
 */

/**
 * The dividers: Velocity and Endurance for all 1,575 companies, as published.
 *
 * Read out of the data rather than typed in, so a new export moves the lines
 * with the numbers. The companies file agrees with it to three decimals — its
 * own means are 2.917 and 2.934 — which is one of the two places the derived
 * formula was confirmed against ground truth.
 */
const MID_V = BM_INDICES.global?.ALL?.velocity ?? 3
const MID_E = BM_INDICES.global?.ALL?.endurance ?? 3

function profileOf(velocity: number, endurance: number): Profile {
  if (velocity >= MID_V) return endurance >= MID_E ? 'ironMan' : 'sprinter'
  return endurance >= MID_E ? 'marathon' : 'walker'
}

/**
 * The plot.
 *
 * **1 to 5 on both axes, ticked at whole numbers, exactly as the original.**
 * That is wider than the data needs — Velocity runs 1.54 to 4.34 — and the
 * instinct is to crop to the data. Two reasons not to: the scale is the
 * instrument's, so a dot's height means something absolute rather than
 * something relative to whoever else is on screen; and the axis must not
 * rescale as you filter, or two sectors 0.1 apart look like two a point apart.
 */
const DOMAIN_MIN = 1
const DOMAIN_MAX = 5
const PAD = { left: 38, right: 10, top: 10, bottom: 34 }
const PLOT = 330
const W = PAD.left + PLOT + PAD.right
const H = PAD.top + PLOT + PAD.bottom
const scale = (v: number) => (v - DOMAIN_MIN) / (DOMAIN_MAX - DOMAIN_MIN)
const px = (v: number) => PAD.left + scale(v) * PLOT
const py = (v: number) => PAD.top + (1 - scale(v)) * PLOT
const TICKS = [1, 2, 3, 4, 5]

/** Beside a figure this page did not measure but worked out. */
const ESTIMATE_MARK = '\u00a0*'

/** How near the pointer has to be, in viewBox units, to pick a dot up. */
const HOVER_RADIUS = 14

interface Company {
  id: string
  country: string
  industry: string
  size: string
  velocity: number
  endurance: number
  daimi: number
  /** Estimated — see `generate-benchmark.mjs`. Null where the group has none. */
  aimi: number | null
  dmi: number | null
}

function Scatter({
  points,
  labels: t,
  counts,
  index,
}: {
  points: Company[]
  labels: SectionOf<'benchmark'>['labels']
  counts: Record<Profile, number>
  index: IndexKey
}) {
  const [hovered, setHovered] = useState<Company | null>(null)

  /**
   * The nearest dot to the pointer, or nothing.
   *
   * **A hover handler per circle would be the obvious way and it is the wrong
   * one here.** At r=2.6 in a 388-unit box the dots are about two CSS pixels
   * across on a phone and four on a laptop, so most attempts to point at one
   * land between them and nothing happens — and 1,575 listeners is 1,575
   * listeners. One handler on the svg, picking the nearest point within
   * `HOVER_RADIUS`, means aiming at a dot is enough rather than hitting it.
   *
   * `pointer` events rather than `mouse`, so a tap on a phone opens the panel
   * too — which is the one thing the original's tooltip could not do.
   */
  const pick = (e: React.PointerEvent<SVGSVGElement>) => {
    const box = e.currentTarget.getBoundingClientRect()
    if (!box.width || !box.height) return
    const x = ((e.clientX - box.left) / box.width) * W
    const y = ((e.clientY - box.top) / box.height) * H
    let best: Company | null = null
    let bestDistance = HOVER_RADIUS * HOVER_RADIUS
    for (const p of points) {
      const dx = px(p.velocity) - x
      const dy = py(p.endurance) - y
      const d = dx * dx + dy * dy
      if (d < bestDistance) {
        bestDistance = d
        best = p
      }
    }
    setHovered(best)
  }

  const at = hovered ? { x: px(hovered.velocity), y: py(hovered.endurance) } : null

  return (
    <figure className="relative rounded-[--radius] bg-muted/40 p-3 sm:p-4">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="h-auto w-full touch-none"
        role="img"
        // The chart is a shape, and a shape read aloud is four numbers. This
        // says what the dots add up to rather than pretending a scatter can be
        // navigated by screen reader — the same four counts are in the legend
        // below as real text, so nothing here is the only copy of anything.
        aria-label={PROFILES.map((k) => `${t.profiles[k].name}: ${counts[k]}`).join('. ')}
        onPointerMove={pick}
        onPointerDown={pick}
        onPointerLeave={() => setHovered(null)}
      >
        {/* Quadrant tint, drawn first so everything else sits on top. Only the
            two ends are tinted, matching the legend's two colours. */}
        <rect
          x={px(MID_V)} y={PAD.top}
          width={W - PAD.right - px(MID_V)} height={py(MID_E) - PAD.top}
          className="fill-primary" opacity={0.06}
        />
        <rect
          x={PAD.left} y={py(MID_E)}
          width={px(MID_V) - PAD.left} height={PAD.top + PLOT - py(MID_E)}
          className="fill-muted" opacity={0.5}
        />

        {TICKS.map((v) => (
          <g key={v}>
            <line
              x1={px(v)} y1={PAD.top} x2={px(v)} y2={PAD.top + PLOT}
              className="stroke-border" strokeWidth={0.5}
            />
            <line
              x1={PAD.left} y1={py(v)} x2={PAD.left + PLOT} y2={py(v)}
              className="stroke-border" strokeWidth={0.5}
            />
            <text
              x={px(v)} y={PAD.top + PLOT + 14}
              textAnchor="middle" className="fill-muted-foreground" fontSize={9}
            >
              {v}
            </text>
            <text
              x={PAD.left - 6} y={py(v) + 3}
              textAnchor="end" className="fill-muted-foreground" fontSize={9}
            >
              {v}
            </text>
          </g>
        ))}

        {/* The two dividers. Dashed, because they are an average and not a
            boundary anybody decided on. */}
        <line
          x1={px(MID_V)} y1={PAD.top} x2={px(MID_V)} y2={PAD.top + PLOT}
          className="stroke-muted-foreground" strokeWidth={1} strokeDasharray="4 3"
        />
        <line
          x1={PAD.left} y1={py(MID_E)} x2={PAD.left + PLOT} y2={py(MID_E)}
          className="stroke-muted-foreground" strokeWidth={1} strokeDasharray="4 3"
        />

        {points.map((p) => (
          <circle
            key={p.id}
            // Marks the data dots apart from the highlight below, which is a
            // `<circle>` too. `check-benchmark.mjs` counts dots to assert "152
            // under Banking", and with a bare `circle` selector a reader whose
            // pointer happened to rest on the chart made that 153 — which the
            // deliberate breakage of the close-on-leave handler surfaced, as a
            // count that was off by one for a reason nothing on screen showed.
            data-dot=""
            cx={px(p.velocity)}
            cy={py(p.endurance)}
            r={2.6}
            // Ahead of the benchmark on the chosen index, or behind it. Two
            // semantic tokens and no computed colour, so nothing here depends
            // on `color-mix` or on a hand-picked hex.
            className={
              (indexValue(p, index) ?? 0) >= INDEX_AVERAGE[index] ? 'fill-primary' : 'fill-border'
            }
            opacity={0.65}
          />
        ))}

        {/* The hovered one, drawn again on top so it is not hidden under its
            neighbours, which at this density it otherwise would be. */}
        {at && (
          <circle
            cx={at.x} cy={at.y} r={5}
            className="fill-primary stroke-background" strokeWidth={1.5}
          />
        )}

        {/* Corner labels inside the plot. */}
        <text x={PAD.left + PLOT - 4} y={PAD.top + 14} textAnchor="end" className="fill-muted-foreground" fontSize={10} fontWeight={600}>
          {t.profiles.ironMan.name}
        </text>
        <text x={PAD.left + 4} y={PAD.top + 14} className="fill-muted-foreground" fontSize={10} fontWeight={600}>
          {t.profiles.marathon.name}
        </text>
        <text x={PAD.left + PLOT - 4} y={PAD.top + PLOT - 6} textAnchor="end" className="fill-muted-foreground" fontSize={10} fontWeight={600}>
          {t.profiles.sprinter.name}
        </text>
        <text x={PAD.left + 4} y={PAD.top + PLOT - 6} className="fill-muted-foreground" fontSize={10} fontWeight={600}>
          {t.profiles.walker.name}
        </text>

        <text x={PAD.left + PLOT / 2} y={H - 4} textAnchor="middle" className="fill-foreground" fontSize={10} fontWeight={600}>
          {t.axisVelocity}
        </text>
        <text
          x={-(PAD.top + PLOT / 2)} y={11}
          transform="rotate(-90)" textAnchor="middle"
          className="fill-foreground" fontSize={10} fontWeight={600}
        >
          {t.axisEndurance}
        </text>
      </svg>

      {/* The seven fields the original's tooltip carried, as an HTML panel
          rather than an SVG `<title>`.

          `<title>` was the first attempt and it is not what Fatih described: it
          is the browser's own tooltip, so it waits about a second, cannot be
          styled, and never appears on a touch screen at all. This follows the
          pointer immediately and survives a tap.

          Positioned as a percentage of the viewBox, which is what makes it
          track the dot at every width without measuring anything, and flipped
          to whichever side has room — a dot near the top right would otherwise
          put its panel outside the figure. */}
      {hovered && at && (
        <div
          className="pointer-events-none absolute z-10 w-max max-w-[15rem] rounded-[--radius] border border-border bg-background p-3 text-xs shadow-lg"
          style={{
            left: `calc(${(at.x / W) * 100}% )`,
            top: `calc(${(at.y / H) * 100}% )`,
            transform: `translate(${at.x > W / 2 ? '-100%' : '0'}, ${at.y > H / 2 ? '-100%' : '0'}) translate(${at.x > W / 2 ? '-10px' : '10px'}, ${at.y > H / 2 ? '-10px' : '10px'})`,
          }}
        >
          <p className="font-semibold">{hovered.id}</p>
          <dl className="mt-1.5 grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5">
            {(
              [
                // Only the index in force. Printing all three here was the
                // shading version's habit; pressing AIMI is supposed to leave
                // AIMI, and a panel that keeps answering the other two
                // contradicts the button that was just pressed.
                [
                  t.indexNames[index] + (index === 'daimi' ? '' : ESTIMATE_MARK),
                  indexValue(hovered, index)?.toFixed(2) ?? '—',
                ],
                [t.axisVelocity, hovered.velocity.toFixed(2)],
                [t.axisEndurance, hovered.endurance.toFixed(2)],
                [t.country, hovered.country],
                [t.industry, hovered.industry],
                [t.size, hovered.size],
              ] as const
            ).map(([k, v]) => (
              <div key={k} className="contents">
                <dt className="text-muted-foreground">{k}</dt>
                <dd className="tabular-nums">{v}</dd>
              </div>
            ))}
          </dl>
        </div>
      )}
    </figure>
  )
}
